import {
  Button,
  Heading,
  Link,
  Spacer,
  Stack,
  Text as ChakraText,
  useDisclosure,
} from '@chakra-ui/react'
import React, { useContext, useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { NavLink } from 'react-router-dom'
import { CachePolicies, useFetch } from 'use-http'
import { BaseLayout } from '..'
import { colors } from '../../../ui-kit'
import { checkPermission, PermissionsEnum } from '../../../utils'
import FreePlanCTA from '../../components/FreePlan'
import CustomPlanCTA from '../../components/CustomPlan'
import PermissionGuard from '../../components/PermissionGuard'
import AddEditUserModal from '../../components/Users/AddEditUserModal'
import UserTable from '../../components/Users/UserTable'
import type { UserGroupResponse, UsersResponse } from '../../interfaces'
import { UserContext } from '../../providers'
import { MainContentHeadingBox, MainContentWrapper } from '../Base/MainContent'
import { FeaturesEnum } from '../../../utils/features'
import useToast from '../../hooks/useToast'

const UserManagementLayout = (): React.ReactElement => {
  const { user } = useContext(UserContext)

  const { t } = useTranslation()
  const toast = useToast()

  const {
    isOpen: isOpenAddUser,
    onOpen: onOpenAddUser,
    onClose: onCloseAddUser,
  } = useDisclosure()

  const [data, setData] = useState<UsersResponse[]>()
  const [groupData, setGroupData] = useState<UserGroupResponse[]>()

  const { request, loading } = useFetch<UsersResponse[]>({
    cachePolicy: CachePolicies.NO_CACHE,
  })

  const { request:groupRequest, loading:groupLoading } = useFetch<UserGroupResponse[]>({
    cachePolicy: CachePolicies.NO_CACHE,
  })

  const getData = async (): Promise<void> => {
    if (user?.clientNumber) {
      const response = await request.get(
        `/api/clients/${user.clientNumber}/users`,
      )

      if (request.error) {
        toast({
          description: t(
            'error_fetching_data',
            'Error in fetching data. Try refreshing the page.',
          ),
          status: 'error',
          isClosable: true,
          duration: 5000,
          position: 'top-right',
          variant: 'outline',
        })
      } else {
        setData(response)
      }
    }
  }

  const getGroupData = async (): Promise<void> => {
    if (
      user?.clientNumber &&
      user.pricingPlan.featuresList.filter(feature =>
        [
          FeaturesEnum.USER_MANAGEMENT,
          FeaturesEnum.ACL_AND_COLLECTIONS,
        ].includes(feature as FeaturesEnum),
      ).length === 2
    ) {
      const response = await groupRequest.get(
        `/api/clients/${user.clientNumber}/groups`,
      )

      if (groupRequest.error) {
        toast({
          description: t(
            'error_fetching_data',
            'Error in fetching data. Try refreshing the page.',
          ),
          status: 'error',
          isClosable: true,
          duration: 5000,
          position: 'top-right',
          variant: 'outline',
        })
      } else {
        setGroupData(response)
      }
    }
  }

  useEffect(()=>{
    getGroupData()
  },[user])

  return (
    <BaseLayout>
      {user?.pricingPlan.featuresList.includes(FeaturesEnum.USER_MANAGEMENT) ? (
        <MainContentHeadingBox>
          <Heading
            as="h2"
            size="md"
            fontWeight="semibold"
            color={colors.appDarkIndigo}
          >
            {t('users', 'Users')}
          </Heading>
          <Spacer />
          <PermissionGuard permission={PermissionsEnum.SELF_TEAM_MANAGE}>
            {(hasPermission: boolean) =>
              hasPermission ? (
                <Link
                  as={NavLink}
                  to="/settings/multi-factor-auth"
                  _focus={{ textDecoration: 'none', outline: 'none' }}
                >
                  {t('enable_global_MFA', 'Enable global MFA')}
                </Link>
              ) : (
                <></>
              )
            }
          </PermissionGuard>
          {checkPermission({
            user,
            permission: PermissionsEnum.SELF_TEAM_MANAGE,
          }) ? (
            <Button marginLeft={8} onClick={onOpenAddUser}>
              {t('add_user', 'Add User')}
            </Button>
          ) : null}
        </MainContentHeadingBox>
      ) : (
        <MainContentHeadingBox>
          <Stack direction={['column']} spacing={1}>
            <Heading
              as="h2"
              size="md"
              fontWeight="600"
              color={colors.appDarkIndigo}
            >
              {t('user_management', 'User Management')}
            </Heading>
            <ChakraText color="appBattleshipGrey">
              {t('user_management_description')}
            </ChakraText>
          </Stack>
        </MainContentHeadingBox>
      )}
      <MainContentWrapper>
        {user?.pricingPlan.featuresList.includes(
          FeaturesEnum.USER_MANAGEMENT,
        ) ? (
          <UserTable
            user={user}
            data={data}
            getData={getData}
            loading={loading}
            groupData={groupData}
            getGroupData={getGroupData}
          />
        ) : user?.pricingPlan.isPaidPlan ? (
          <CustomPlanCTA feature={FeaturesEnum.USER_MANAGEMENT} />
        ) : (
          <FreePlanCTA />
        )}
      </MainContentWrapper>
      <AddEditUserModal
        isEdit={false}
        user={user}
        isOpen={isOpenAddUser}
        onClose={onCloseAddUser}
        getData={getData}
        groupData={groupData}
        getGroupData={getGroupData}
      />
    </BaseLayout>
  )
}

export default UserManagementLayout
